// =============================================================================
// components/create/CreateSwipeGesture.tsx — Instagram-style edge swipe → the
// FULL-SCREEN CreateHub.
//
// Wraps the Home tab AT THE NAVIGATOR LEVEL (navigation/Tabs.tsx composes
// <CreateSwipeGesture><HomeScreen/></CreateSwipeGesture>; home.tsx untouched).
//
// ARCHITECTURE (client feedback, device-tested):
//   • NO permanent chrome. The old always-visible right-edge glass sliver
//     ("light bubble") is GONE. Discoverability = CreateSwipeHint, a brief
//     self-dismissing pill shown only on the first 2 Home visits per install
//     (AsyncStorage counter), plus the hub itself following the finger the
//     moment a swipe starts.
//   • The pan DIRECTLY DRIVES a full-screen overlay <Animated.View> hosting
//     CreateHub: progress 0→1 maps to translateX screenWidth→0, so the hub
//     tracks the finger 1:1 (reanimated shared value, UI thread — no JS-frame
//     lag on bridgeless/New Arch). Release past the distance/velocity threshold
//     springs it open; otherwise it snaps back. Exactly Instagram's camera pane.
//   • Home gets a subtle parallax (slides left ~18% + dims) under the incoming
//     hub — the two surfaces read as one connected gesture.
//   • While the hub is open: swipe RIGHT anywhere (or tap its X, or Android
//     back) slides it away with the same interactive tracking.
//
// Gesture design (gesture-handler Pan + reanimated):
//   - hitSlop {right:0, width:EDGE_HIT_WIDTH} restricts where the open-pan may
//     BEGIN to a strip along the right edge, so it never fights the feed's
//     horizontal rails (stories carousel) mid-screen.
//   - activeOffsetX(-14) / failOffsetX(14): only leftward drags activate.
//   - failOffsetY(±16): vertical movement hands the touch to the feed scroll.
//   - Commit on distance (≥ OPEN_DISTANCE) or a flick (|velocity| ≥ FLICK).
// =============================================================================
import React, { useCallback, useEffect, useState } from 'react';
import { BackHandler, StyleSheet, View, useWindowDimensions } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  Easing,
  cancelAnimation,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { Ionicons } from '../ui/Icon';
import { glassTokens } from '../glass/tokens';
import { animationTokens, colors, spacing } from '../../constants/theme';
import CreateHub from './CreateHub';
import CreateSwipeHint from './CreateSwipeHint';

// Width of the right-edge strip where the open-swipe may begin.
const EDGE_HIT_WIDTH = 32;
// Drag distance (pt) that commits the open (or the close, mirrored).
const OPEN_DISTANCE = 96;
// Or a flick at least this fast (pt/s).
const FLICK_VELOCITY = 800;
// First-run discoverability: hint shows on the first N Home visits per install.
const HINT_KEY = 'cryb:create_swipe_hint_shown';
const HINT_MAX_SHOWS = 2;

const SPRING = { damping: 26, stiffness: 280, overshootClamping: true } as const;

// ── Animated edge cue ────────────────────────────────────────────────────────
// A subtle, self-looping chevron that "breathes" leftward from the right edge to
// TEACH the create-swipe direction — improving discoverability beyond the static
// text hint, still with zero permanent chrome (it only rides alongside the
// first-run CreateSwipeHint, then unmounts with it). Pure reanimated on the UI
// thread; tokenised motion + brand glass tint.
const CUE_TRAVEL = spacing.md; // how far the chevron drifts left each pulse

function SwipeEdgeCue() {
  // 0 = rest at the edge, 1 = drifted left + brightest (mid-pulse).
  const pulse = useSharedValue(0);

  useEffect(() => {
    pulse.value = withRepeat(
      withSequence(
        withTiming(1, {
          duration: animationTokens.duration.slow,
          easing: Easing.out(Easing.cubic),
        }),
        withTiming(0, {
          duration: animationTokens.duration.base,
          easing: Easing.in(Easing.cubic),
        }),
        // brief hold before the next nudge so it reads as a deliberate cue
        withDelay(animationTokens.duration.slow, withTiming(0, { duration: 0 })),
      ),
      -1,
      false,
    );
    return () => cancelAnimation(pulse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const style = useAnimatedStyle(() => ({
    opacity: 0.35 + pulse.value * 0.5,
    transform: [{ translateX: pulse.value * CUE_TRAVEL }],
  }));

  return (
    <Animated.View pointerEvents="none" style={[styles.cue, style]}>
      <Ionicons name="chevron-forward" size={20} color={colors.primary} />
    </Animated.View>
  );
}

export default function CreateSwipeGesture({ children }: { children: React.ReactNode }) {
  const { width } = useWindowDimensions();

  // 0 = hub fully off-screen (closed) … 1 = hub fully on-screen (open).
  // The pan writes this directly, so the hub follows the finger.
  const progress = useSharedValue(0);
  // JS mirror of "hub is open" — gates pointerEvents, Android back, the hint.
  const [open, setOpen] = useState(false);

  const openHub = useCallback(() => {
    setOpen(true);
    progress.value = withSpring(1, SPRING);
  }, [progress]);

  const closeHub = useCallback(() => {
    setOpen(false);
    progress.value = withSpring(0, SPRING);
  }, [progress]);

  // After a mode routed into its real flow (modal now covering us): reset the
  // overlay INSTANTLY (no slide-out flash) so Home is clean on return.
  const resetHub = useCallback(() => {
    setOpen(false);
    progress.value = 0;
  }, [progress]);

  // ---- First-2-visits hint (the ONLY idle affordance; never permanent) ------
  const [showHint, setShowHint] = useState(false);
  useEffect(() => {
    let cancelled = false;
    AsyncStorage.getItem(HINT_KEY)
      .then((raw) => {
        const shown = raw ? parseInt(raw, 10) || 0 : 0;
        if (shown < HINT_MAX_SHOWS && !cancelled) {
          setShowHint(true);
          return AsyncStorage.setItem(HINT_KEY, String(shown + 1));
        }
        return undefined;
      })
      .catch(() => {
        // Storage unavailable → fail silent, no hint. Never block Home.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const dismissHint = useCallback(() => setShowHint(false), []);
  const hintOpen = useCallback(() => {
    setShowHint(false);
    openHub();
  }, [openHub]);

  // ---- Android hardware back closes the hub --------------------------------
  useEffect(() => {
    if (!open) return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      closeHub();
      return true;
    });
    return () => sub.remove();
  }, [open, closeHub]);

  // ---- OPEN pan: leftward drag from the right edge, hub tracks the finger ---
  // enabled(!open): once the hub is up, a fresh edge swipe must not restart the
  // open-tracking from 0 (which would teleport the hub off-screen).
  const openPan = Gesture.Pan()
    .enabled(!open)
    // Instagram-style: swipe RIGHT from the LEFT edge opens the create/camera hub.
    .hitSlop({ left: 0, width: EDGE_HIT_WIDTH })
    .activeOffsetX(14)
    .failOffsetX(-14)
    .failOffsetY([-16, 16])
    .onUpdate((e) => {
      progress.value = Math.min(Math.max(e.translationX / width, 0), 1);
    })
    .onEnd((e) => {
      const commit = e.translationX >= OPEN_DISTANCE || e.velocityX >= FLICK_VELOCITY;
      if (commit) {
        progress.value = withSpring(1, SPRING);
        runOnJS(setOpen)(true);
      } else {
        progress.value = withSpring(0, SPRING);
      }
    })
    .onFinalize((_e, success) => {
      // Cancelled/failed after partially tracking → settle closed.
      if (!success) {
        progress.value = withSpring(0, SPRING);
      }
    });

  // ---- CLOSE pan: rightward drag anywhere on the open hub -------------------
  const closePan = Gesture.Pan()
    .enabled(open)
    // Mirror: swipe LEFT anywhere on the open hub to close it.
    .activeOffsetX(-18)
    .failOffsetY([-20, 20])
    .onUpdate((e) => {
      progress.value = Math.min(Math.max(1 + e.translationX / width, 0), 1);
    })
    .onEnd((e) => {
      const commit = e.translationX <= -OPEN_DISTANCE || e.velocityX <= -FLICK_VELOCITY;
      if (commit) {
        progress.value = withSpring(0, SPRING);
        runOnJS(setOpen)(false);
      } else {
        progress.value = withSpring(1, SPRING);
      }
    })
    .onFinalize((_e, success) => {
      if (!success) {
        progress.value = withSpring(1, SPRING);
      }
    });

  // Hub overlay: slides in from the LEFT, 1:1 with progress.
  const hubStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: -(1 - progress.value) * width }],
  }));
  // Home parallax + dim under the incoming hub (Instagram's connected feel):
  // Home slides RIGHT as the hub enters from the left.
  const contentStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: progress.value * width * 0.18 }],
  }));
  const veilStyle = useAnimatedStyle(() => ({
    opacity: progress.value * 0.28,
  }));

  return (
    <GestureDetector gesture={openPan}>
      {/* collapsable={false}: the detector needs a real native view. */}
      <View style={styles.host} collapsable={false}>
        <Animated.View style={[styles.content, contentStyle]}>{children}</Animated.View>

        {/* Dim veil over Home while the hub slides across (non-interactive). */}
        <Animated.View pointerEvents="none" style={[styles.veil, veilStyle]} />

        {/* Brief first-2-visits hint — the ONLY idle affordance, self-dismissing.
            The animated edge cue rides alongside it to TEACH the swipe direction
            (leftward-drifting chevron), then unmounts with the hint. */}
        {showHint && !open && (
          <>
            <SwipeEdgeCue />
            <CreateSwipeHint onOpen={hintOpen} onDone={dismissHint} />
          </>
        )}

        {/* Full-screen CreateHub overlay. Transforms move the hit area with it,
            so off-screen it can't intercept Home touches; pointerEvents is still
            gated on `open` as a belt-and-braces guard mid-spring. */}
        <GestureDetector gesture={closePan}>
          <Animated.View
            style={[styles.hub, hubStyle]}
            pointerEvents={open ? 'auto' : 'none'}
            collapsable={false}
          >
            <CreateHub onClose={closeHub} onLaunched={resetHub} />
          </Animated.View>
        </GestureDetector>
      </View>
    </GestureDetector>
  );
}

const styles = StyleSheet.create({
  host: {
    flex: 1,
    backgroundColor: colors.bg, // parallax reveals the seam — keep it on-brand
    overflow: 'hidden',
  },
  content: {
    flex: 1,
  },
  veil: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.ink,
  },
  hub: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  // Animated direction cue: vertically centred, hugging the right edge just left
  // of the CreateSwipeHint pill (which sits at right:0). Glass-tinted, subtle.
  cue: {
    position: 'absolute',
    left: glassTokens.radius.xl,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
});
